import type { components } from "@/lib/api/schema";

/**
 * Canonical role codes, tied to the OpenAPI-generated `RoleEnum`. If the
 * backend ever adds or removes a role, `schema.d.ts` regenerates and this
 * object will fail TypeScript compilation — forcing an explicit acknowledgement
 * rather than silent drift.
 *
 * Display labels live on the server (see ``useRoleCatalog``) — do not add
 * them here.
 */
export type RoleType = components["schemas"]["RoleEnum"];

export const Role: Record<
  | "PLATFORM_ADMIN"
  | "PLATFORM_MANAGER"
  | "DISTRIBUTOR_ADMIN"
  | "MSP_ADMIN"
  | "COMPANY_OWNER"
  | "COMPANY_ADMIN"
  | "TEAM_MEMBER"
  | "ACCOUNTING"
  | "READ_ONLY",
  RoleType
> = {
  PLATFORM_ADMIN: "platform_admin",
  PLATFORM_MANAGER: "platform_manager",
  DISTRIBUTOR_ADMIN: "distributor_admin",
  MSP_ADMIN: "msp_admin",
  COMPANY_OWNER: "company_owner",
  COMPANY_ADMIN: "company_admin",
  TEAM_MEMBER: "team_member",
  ACCOUNTING: "accounting",
  READ_ONLY: "read_only",
};

/** Matches backend `CanManageGroups` (platform, distributor, MSP, company admins). */
const PERMISSION_GROUP_MANAGER_ROLES: readonly string[] = [
  Role.PLATFORM_ADMIN,
  Role.PLATFORM_MANAGER,
  Role.DISTRIBUTOR_ADMIN,
  Role.MSP_ADMIN,
  Role.COMPANY_OWNER,
  Role.COMPANY_ADMIN,
];

export function canManagePermissionGroups(role: string | undefined): boolean {
  return role != null && PERMISSION_GROUP_MANAGER_ROLES.includes(role);
}

/**
 * Matches backend `CanManageIntegrations`, the permission on every
 * integrations endpoint: platform, distributor, MSP and company admins (a
 * company's owner and its admins alike).
 */
const INTEGRATION_MANAGER_ROLES: readonly string[] = [
  Role.PLATFORM_ADMIN,
  Role.PLATFORM_MANAGER,
  Role.DISTRIBUTOR_ADMIN,
  Role.MSP_ADMIN,
  Role.COMPANY_OWNER,
  Role.COMPANY_ADMIN,
];

export function canManageIntegrations(role: string | undefined): boolean {
  return role != null && INTEGRATION_MANAGER_ROLES.includes(role);
}
